import { useCallback, useEffect, useRef, useState } from "react";
import fetchApiKey from "../../fetchApiKey";
import {
    workflowAuthorizedApiKeysFamily,
    workspaceAuthorizedApiKeysFamily
} from "../state/ApiKeyState";
import { ApiKeyWithFolders, Workflow } from "../../../state/types";
import { useRecoilValue, useSetRecoilState } from "recoil";
import authApi from "../../../backendapi/authApi";

/**
 * Hook to get all API keys for a workspace with real-time Firebase sync.
 * Returns apiKeys (null while loading, empty array if no keys exist) and a refresh function.
 *
 * @param workspaceId - The workspace ID to fetch API keys for
 * @param enabled - If false, skips fetching and returns null (default: true)
 * @param options.includeDisabled - If true, includes disabled (but not revoked) keys (default: false)
 * @param options.folderIds - If provided, filters keys by folder IDs
 */
export const useAuthorizedWorkspaceApiKeys = (
    workspaceId: string,
    enabled: boolean = true,
    {
        includeDisabled = false,
        folderIds = []
    }: { includeDisabled?: boolean; folderIds?: string[] } = {}
): { apiKeys: ApiKeyWithFolders[] | null; refresh: () => Promise<void> } => {
    const atomParam = { workspaceId: enabled ? workspaceId : "", includeDisabled, folderIds };

    // Always call hooks unconditionally, but use a dummy value when disabled
    const apiKeys = useRecoilValue(workspaceAuthorizedApiKeysFamily(atomParam));
    const setApiKeys = useSetRecoilState(workspaceAuthorizedApiKeysFamily(atomParam));

    const refresh = useCallback(async () => {
        if (!enabled || !workspaceId) {
            return;
        }

        const result: { apiKeys: ApiKeyWithFolders[] } =
            await authApi.getAuthorizedApiKeysForWorkspace(workspaceId, {
                includeFolders: true,
                includeDisabled,
                folderIds
            });

        setApiKeys(result.apiKeys);
    }, [workspaceId, enabled, includeDisabled, folderIds, setApiKeys]);

    // Return null when disabled to indicate "not applicable" state
    if (!enabled) {
        return { apiKeys: null, refresh };
    }

    return { apiKeys, refresh };
};

// https://stackoverflow.com/questions/56262515/how-to-handle-dependencies-array-for-custom-hooks-in-react
export const useApiKey = ({
    workspaceId,
    projectGroupId,
    dependencyCallback,
    enabled = true
}: {
    workspaceId?: string;
    projectGroupId?: string;
    dependencyCallback?: () => void;
    enabled?: boolean;
} = {}) => {
    const [apiKey, setApiKey] = useState<string | undefined>();

    const fetchAndSetApiKey = useCallback(async () => {
        setApiKey(
            await fetchApiKey({ restricted: { HAS_COOKIE: true } })(
                workspaceId || window.currentWorkspace,
                { projectGroupId }
            )
        );
    }, [workspaceId, projectGroupId]);

    useEffect(() => {
        if (enabled) fetchAndSetApiKey();
    }, [enabled, dependencyCallback]);

    return apiKey;
};

export const useAuthorizedApiKeysForWorkflow = (
    workflowId: string,
    enabled: boolean = true,
    {
        includeFolders = false,
        workspaceId,
        workflow
    }: { includeFolders?: boolean; workspaceId?: string; workflow?: Partial<Workflow> } = {}
): { apiKeys: ApiKeyWithFolders[] | null; refresh: () => Promise<void> } => {
    const atomParam = {
        workflowId: enabled ? workflowId : "",
        workspaceId: enabled ? workspaceId : "",
        includeFolders
    };
    const apiKeys = useRecoilValue(workflowAuthorizedApiKeysFamily(atomParam));
    const setApiKeys = useSetRecoilState(workflowAuthorizedApiKeysFamily(atomParam));

    // Store the latest workflow in a ref so refresh() always uses the current value
    const workflowRef = useRef(workflow);
    workflowRef.current = workflow;

    const refresh = useCallback(async () => {
        if (!enabled || !workflowId) {
            return;
        }

        const result: { apiKeys: ApiKeyWithFolders[] } =
            await authApi.getAuthorizedApiKeysForWorkflow(workflowId, {
                includeFolders,
                workflow: workflowRef.current
            });

        setApiKeys(result.apiKeys);
    }, [workflowId, enabled, includeFolders, setApiKeys]);

    if (!enabled) {
        return { apiKeys: null, refresh };
    }

    return { apiKeys, refresh };
};
